import { z } from "zod";
import { sessionApprovalDecisionSchema, sessionApprovalStatusSchema } from "./sessions.js";
import { twinSessionSummarySchema } from "./twin-public-sessions.js";
import { twinSessionTurnSchema } from "./twin-queries.js";
/**
 * Stopping one of the person's own sessions (twin.sessions.cancel): the turn it is answering, or with `queuedRunId`
 * only a follow-up still queued behind that turn. A session with nothing to stop answers the same.
 */
export const twinSessionCancelInputSchema = z.object({
    queuedRunId: z.string().trim().min(1).max(200).optional(),
}).strict();
export const twinSessionCancelResponseSchema = z.object({ ok: z.literal(true) }).strict();
/**
 * What changed in a session the person may read (twin.session-progress.list), after a cursor on the session's own
 * event sequence: each turn that changed, as it stands. A turn still queued or running carries what it has said so far
 * and how many tool steps it has taken; its answer, once it has one, is `assistantResponse`. Pass `cursor` back as
 * `afterSeq` until `isDone`. A cursor above `tip` is too old for this session: read the session again.
 */
export const twinSessionProgressQuerySchema = z.object({
    afterSeq: z.coerce.number().int().nonnegative().optional(),
}).strict();
export const twinSessionProgressTurnSchema = twinSessionTurnSchema.extend({
    progress: z.object({
        text: z.string(),
        steps: z.number().int().nonnegative(),
    }).strict().optional(),
}).strict();
export const twinSessionProgressResponseSchema = z.object({
    session: twinSessionSummarySchema,
    /** The turns that changed after the cursor, oldest first. */
    turns: z.array(twinSessionProgressTurnSchema).max(20),
    cursor: z.number().int().nonnegative(),
    isDone: z.boolean(),
    /** The session's latest event. */
    tip: z.number().int().nonnegative(),
}).strict();
/** A tool call the Twin's agent asks the person to approve before it runs, in one of their own sessions. */
export const twinApprovalSchema = z.object({
    id: z.string().min(1),
    sessionId: z.string().min(1),
    /** The tool the agent asks to run, and what it proposes to pass it. */
    toolName: z.string().min(1),
    proposedArgs: z.unknown(),
    /** Why the policy asks first. */
    reason: z.string().optional(),
    status: sessionApprovalStatusSchema,
    decidedAt: z.number().int().nonnegative().optional(),
    createdAt: z.number().int().nonnegative(),
}).strict();
/** The approvals in the person's own sessions (twin.approvals.list), newest first: by default those waiting on them. */
export const twinApprovalsListQuerySchema = z.object({
    status: sessionApprovalStatusSchema.default("pending"),
    sessionId: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict();
export const twinApprovalsListResponseSchema = z.object({
    approvals: z.array(twinApprovalSchema).max(100),
}).strict();
/**
 * The person's decision on an approval in one of their own sessions (twin.approvals.decide). It is the person's alone,
 * so it is no MCP tool: an agent may ask, never answer.
 */
export const twinApprovalDecideInputSchema = z.object({
    decision: sessionApprovalDecisionSchema,
    comment: z.string().trim().min(1).max(2_000).optional(),
}).strict();
export const twinApprovalDecideResponseSchema = z.object({ approval: twinApprovalSchema }).strict();
